<template>
  <MainContainer id="vue-app">
    <ThemeProvider :theme="theme">
      <Loader v-if="loading" />
      <div v-if="dataSourceData.length">
        <Title v-if="title">{{ title }}</Title>
        <InnerContainer>
          <VisualisationSwitcher />
          <YearSlider :data="dataSourceData" />
        </InnerContainer>
        <MapComponent v-if="showChart(CHARTS.REGIONAL_MAP)" :region="mapType" :theme="theme" :data="dataSourceData" />
        <InnerContainer>
          <AtAGlanceTable v-if="showChart(CHARTS.GLANCE_TABLE)" :data-source="dataSourceData" />
          <OverviewTable v-if="showChart(CHARTS.TABLE)" :dataSource="dataSourceData" />
          <LineChart
            v-if="showChart(CHARTS.LINE_CHART)"
            :data="dataSourceData"
            :title="lineChartTitle.title"
            :subtitle="lineChartTitle.subtitle"
            :theme="theme"
          />
          <TreeMap v-if="showChart(CHARTS.TREEMAP)" :data="dataSourceData" :theme="theme" />
          <ShareIcons />
          <DataSourceComponent :source="source" />
        </InnerContainer>
        <SummaryBar v-if="showChart(CHARTS.REGIONAL_MAP)" />
      </div>
      <NoData v-if="!loading && !dataSourceData.length" />
    </ThemeProvider>
  </MainContainer>
</template>

<script>
import { filter, pathEq, pathOr, cond, always, equals, T, isEmpty } from 'ramda';
import { ThemeProvider } from 'vue-styled-components';
import { REGIONS, CHARTS, ASIA, SCHEMA_CMS_PROJECT_ID, LIGHT_PREFIX } from '@shared/constants';
import { REGIONS_COLOR } from '@shared/colors';
import { MapComponent } from '@shared/components/map';
import { SummaryBar } from '@shared/components/summaryBar';
import { AtAGlanceTable } from '@shared/components/ataGlanceTable';

import { YearSlider } from './shared/components/yearSlider';
import { VisualisationSwitcher } from './shared/components/visualisationSwitcher';
import { OverviewTable } from './shared/components/overviewTable';
import { LineChart } from './shared/components/lineChart';
import { TreeMap } from './shared/components/treeMap';
import { DataSourceComponent } from './shared/components/dataSource';
import { ShareIcons } from './shared/components/shareIcons';
import api from './shared/services/api';
import { MainContainer, InnerContainer, Title } from './app.styles';
import { mapActions, mapGetters, mapMutations } from 'vuex';
import { PREFIX as REGIONS_STORE } from './modules/regions';
import {
  SET_TOPICS,
  SET_SELECTED_TOPIC,
  SET_SELECTED_SUBTOPIC,
  SET_LOADING,
} from './modules/regions/regions.mutations';
import { FETCH_DATA } from './modules/regions/regions.actions';
import { GET_DATA, GET_LOADING, GET_SELECTED_FILTERS, GET_SELECTED_SUBTOPIC } from './modules/regions/regions.getters';
import { getRegionData, getRegionField } from '@shared/utils/helpers';
import { Loader } from './shared/components/loader';
import { NoData } from '@shared/components/noData';
import { DROPDOWN_PREFIX } from '@shared/constants';

export default {
  name: 'App',
  created() {
    (async () => {
      const [
        ,
        ASIA_MATTERS_REGION = '',
        ,
        ASIA_MATTERS_TOPIC = '',
        ASIA_MATTERS_SUBTOPIC = '',
      ] = window.location.pathname.split('/');

      this.region = isEmpty(ASIA_MATTERS_REGION) ? ASIA : ASIA_MATTERS_REGION;
      const topic = isEmpty(ASIA_MATTERS_TOPIC) ? null : ASIA_MATTERS_TOPIC;
      const subtopic = isEmpty(ASIA_MATTERS_SUBTOPIC) ? null : ASIA_MATTERS_SUBTOPIC;

      const {
        data: { dataSources },
      } = await api.get(`projects/${SCHEMA_CMS_PROJECT_ID}`);
      const dataSourcesFilterByRegion = filter(pathEq(['tags', 'regions', 0], this.region))(dataSources);

      const { topics, defaultTopic, defaultSubtopic } = getRegionData({ topic, subtopic, dataSourcesFilterByRegion });

      if (dataSourcesFilterByRegion.length) {
        this[SET_TOPICS](topics);
        this[SET_SELECTED_TOPIC](defaultTopic.topic);
        this[SET_SELECTED_SUBTOPIC](defaultSubtopic);
        this[FETCH_DATA](defaultSubtopic.id);
        return;
      }

      this[SET_LOADING](false);
    })();
  },
  methods: {
    ...mapMutations(REGIONS_STORE, [SET_TOPICS, SET_SELECTED_TOPIC, SET_SELECTED_SUBTOPIC, SET_LOADING]),
    ...mapActions(REGIONS_STORE, [FETCH_DATA]),

    showChart(chart) {
      return this.charts.includes(chart);
    },
  },
  components: {
    ThemeProvider,
    YearSlider,
    VisualisationSwitcher,
    OverviewTable,
    AtAGlanceTable,
    MapComponent,
    DataSourceComponent,
    SummaryBar,
    LineChart,
    TreeMap,
    ShareIcons,
    MainContainer,
    InnerContainer,
    Loader,
    NoData,
    Title,
  },
  data() {
    return {
      REGIONS,
      CHARTS,
    };
  },
  computed: {
    ...mapGetters(REGIONS_STORE, [GET_DATA, GET_SELECTED_SUBTOPIC, GET_SELECTED_FILTERS, GET_LOADING]),
    charts() {
      return pathOr([], ['tags', 'charts'], this[GET_SELECTED_SUBTOPIC]);
    },
    loading() {
      return this[GET_LOADING];
    },
    mapType() {
      return pathOr([], ['tags', 'mapType', 0], this[GET_SELECTED_SUBTOPIC]);
    },
    title() {
      return pathOr('', ['meta', 'customData', 'title'], this[GET_SELECTED_SUBTOPIC]);
    },
    source() {
      return pathOr('', ['meta', 'customData', 'source'], this[GET_SELECTED_SUBTOPIC]);
    },
    lineChartTitle() {
      const filters = this[GET_SELECTED_FILTERS];
      const subtopic = 'A Look at ' + pathOr('', ['meta', 'customData', 'subtopic'], this[GET_SELECTED_SUBTOPIC]);
      const region = getRegionField(this[GET_SELECTED_SUBTOPIC]);

      return cond([
        [equals(1), always({ title: subtopic + ' in ', subtitle: filters[0] })],
        [equals(0), always({ title: subtopic })],
        [T, always({ title: subtopic + ' by ', subtitle: region })],
      ])(filters.length);
    },
    dataSourceData() {
      return this[GET_DATA];
    },
    theme() {
      return {
        mainColor: REGIONS_COLOR[this.region],
        lightColor: REGIONS_COLOR[`${LIGHT_PREFIX}${this.region}`],
        dropdownColor: REGIONS_COLOR[`${DROPDOWN_PREFIX}${this.region}`],
      };
    },
  },
};
</script>
